import {
    Autocomplete,
    AutocompleteRenderInputParams,
    AutocompleteRenderOptionState,
    AutocompleteValue,
    Box,
    Checkbox,
    Chip,
    TextField,
} from "@mui/material";
import { ReactNode, useCallback, useMemo } from "react";

// Generic option interface
export interface AppAutoCompleteOption<T = any> {
    id: string | number;
    label: string;
    value: T;
    group?: string;
    disabled?: boolean;
    isGroupHeader?: boolean; // Flag to indicate this is a group header (parent)
    [key: string]: any; // Allow additional properties
}

// Props interface
export interface AppAutoCompleteProps<T, Multiple extends boolean = false> {
    // Basic props
    label: string;
    placeholder?: string;
    options: AppAutoCompleteOption<T>[];
    value?: Multiple extends true ? T[] : T | null;
    onChange?: (value: Multiple extends true ? T[] : T | null) => void;
    error?: boolean;
    helperText?: string;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;

    // Selection mode
    multiple?: Multiple;
    disableCloseOnSelect?: boolean;

    // Display options
    showCheckboxes?: boolean;
    showChips?: boolean;
    chipVariant?: "filled" | "outlined";
    groupBy?: (option: AppAutoCompleteOption<T>) => string;
    enableSelectableGroupHeaders?: boolean; // Allow selecting group headers (parents)

    // Custom rendering
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: AppAutoCompleteOption<T>,
        state: AutocompleteRenderOptionState
    ) => ReactNode;
    renderTags?: (
        value: AppAutoCompleteOption<T>[],
        getTagProps: (params: { index: number }) => any
    ) => ReactNode;
    renderInput?: (params: AutocompleteRenderInputParams) => ReactNode;

    // Filtering and search
    filterOptions?: (
        options: AppAutoCompleteOption<T>[],
        { inputValue }: { inputValue: string }
    ) => AppAutoCompleteOption<T>[];
    noOptionsText?: string;
    loading?: boolean;
    loadingText?: string;

    // Additional props
    size?: "small" | "medium";
    variant?: "outlined" | "filled" | "standard";
    sx?: any;

    // Value conversion helpers
    getOptionValue?: (option: AppAutoCompleteOption<T>) => any;
    findOptionByValue?: (value: any) => AppAutoCompleteOption<T> | undefined;
}

export default function AppAutoComplete<T, Multiple extends boolean = false>({
    label,
    placeholder,
    options,
    value,
    onChange,
    error = false,
    helperText,
    required = false,
    disabled = false,
    fullWidth = true,
    multiple = false as Multiple,
    disableCloseOnSelect = false,
    showCheckboxes = false,
    showChips = true,
    chipVariant = "outlined",
    groupBy,
    enableSelectableGroupHeaders = false,
    renderOption,
    renderTags,
    renderInput,
    filterOptions,
    noOptionsText = "نتیجه‌ای یافت نشد",
    loading = false,
    loadingText = "در حال بارگذاری...",
    size = "medium",
    variant = "outlined",
    sx,
    getOptionValue,
    findOptionByValue,
}: AppAutoCompleteProps<T, Multiple>) {
    // Memoize options conversion to prevent unnecessary re-renders
    const autocompleteOptions = useMemo(
        () =>
            options.map((option) => ({
                ...option,
                title: option.label,
            })),
        [options]
    );

    // Create a Map for O(1) value lookups instead of O(n) searches
    const valueToOptionMap = useMemo(() => {
        const map = new Map();
        autocompleteOptions.forEach((option) => {
            const optionValue = getOptionValue
                ? getOptionValue(option)
                : option.value;
            map.set(optionValue, option);
        });
        return map;
    }, [autocompleteOptions, getOptionValue]);

    // Optimized value comparison function
    const isValueEqual = useCallback((val1: any, val2: any): boolean => {
        if (val1 === val2) return true;
        if (val1 == null || val2 == null) return false;

        // For objects, use deep comparison only when necessary
        if (typeof val1 === "object" && typeof val2 === "object") {
            // Try reference equality first
            if (val1 === val2) return true;

            // For arrays, compare length and elements
            if (Array.isArray(val1) && Array.isArray(val2)) {
                if (val1.length !== val2.length) return false;
                return val1.every((item, index) =>
                    isValueEqual(item, val2[index])
                );
            }

            // For objects, compare keys and values
            const keys1 = Object.keys(val1);
            const keys2 = Object.keys(val2);
            if (keys1.length !== keys2.length) return false;
            return keys1.every((key) => isValueEqual(val1[key], val2[key]));
        }

        return false;
    }, []);

    // Handle value conversion for MUI Autocomplete with optimized performance
    const autocompleteValue = useMemo((): AutocompleteValue<
        any,
        Multiple,
        false,
        false
    > => {
        if (multiple) {
            if (!value || !Array.isArray(value)) return [] as any;

            // Use Map for O(1) lookups instead of O(n²) filter/some
            const selectedOptions: AppAutoCompleteOption<T>[] = [];
            const valueSet = new Set(value);

            for (const option of autocompleteOptions) {
                const optionValue = getOptionValue
                    ? getOptionValue(option)
                    : option.value;
                if (
                    valueSet.has(optionValue) ||
                    Array.from(valueSet).some((val) =>
                        isValueEqual(val, optionValue)
                    )
                ) {
                    selectedOptions.push(option);
                }
            }

            return selectedOptions as any;
        } else {
            if (!value) return null as any;

            if (findOptionByValue) {
                const foundOption = findOptionByValue(value);
                return foundOption as any;
            }

            // Use Map for O(1) lookup
            const foundOption = valueToOptionMap.get(value);
            if (foundOption) return foundOption as any;

            // Fallback to linear search for complex comparisons
            const foundOptionFallback = autocompleteOptions.find((option) => {
                const optionValue = getOptionValue
                    ? getOptionValue(option)
                    : option.value;
                return isValueEqual(value, optionValue);
            });

            return foundOptionFallback as any;
        }
    }, [
        value,
        multiple,
        autocompleteOptions,
        getOptionValue,
        findOptionByValue,
        valueToOptionMap,
        isValueEqual,
    ]);

    // Handle change events with memoization
    const handleChange = useCallback(
        (
            event: React.SyntheticEvent,
            newValue: AutocompleteValue<any, Multiple, false, false>
        ) => {
            if (!onChange) return;

            if (multiple) {
                const selectedValues = (
                    newValue as AppAutoCompleteOption<T>[]
                ).map((option) =>
                    getOptionValue ? getOptionValue(option) : option.value
                );
                onChange(selectedValues as any);
            } else {
                const selectedValue = newValue
                    ? getOptionValue
                        ? getOptionValue(newValue as AppAutoCompleteOption<T>)
                        : (newValue as AppAutoCompleteOption<T>).value
                    : null;
                onChange(selectedValue as any);
            }
        },
        [onChange, multiple, getOptionValue]
    );

    // Default render option with checkbox support and proper value matching
    const defaultRenderOption = useCallback(
        (
            props: React.HTMLAttributes<HTMLLIElement>,
            option: AppAutoCompleteOption<T>,
            state: AutocompleteRenderOptionState
        ) => {
            if (renderOption) {
                return renderOption(props, option, state);
            }

            // Use the same value comparison logic as the main component
            const optionValue = getOptionValue
                ? getOptionValue(option)
                : option.value;
            const isSelected = multiple
                ? Array.isArray(value) &&
                  value.some((val) => isValueEqual(val, optionValue))
                : isValueEqual(value, optionValue);

            // Check if this is a group header (parent) option
            const isGroupHeader = option.isGroupHeader || false;

            // Style group headers and children differently
            const optionStyle = enableSelectableGroupHeaders
                ? isGroupHeader
                    ? {
                          fontWeight: "bold" as const,
                          backgroundColor: "rgba(0, 0, 0, 0.04)",
                      }
                    : {
                          paddingLeft: "32px !important", // Indent children
                      }
                : {};

            return (
                <Box
                    component="li"
                    {...props}
                    key={option.id} // Use option.id as unique key to prevent duplicate key warnings
                    sx={{
                        ...optionStyle,
                        ...props.style,
                    }}
                >
                    {showCheckboxes && (
                        <Checkbox
                            style={{ marginRight: 8 }}
                            checked={isSelected}
                        />
                    )}
                    {option.label}
                </Box>
            );
        },
        [
            renderOption,
            multiple,
            value,
            getOptionValue,
            isValueEqual,
            showCheckboxes,
            groupBy,
            enableSelectableGroupHeaders,
        ]
    );

    // Default render tags for multiple selection with memoization
    const defaultRenderTags = useCallback(
        (
            value: AppAutoCompleteOption<T>[],
            getTagProps: (params: { index: number }) => any
        ) => {
            if (renderTags) {
                return renderTags(value, getTagProps);
            }

            if (!showChips) {
                return null;
            }

            return value.map((option, index) => (
                <Chip
                    key={option.id}
                    variant={chipVariant}
                    label={option.label}
                    {...getTagProps({ index })}
                />
            ));
        },
        [renderTags, showChips, chipVariant]
    );

    // Default render input with memoization
    const defaultRenderInput = useCallback(
        (params: AutocompleteRenderInputParams) => {
            if (renderInput) {
                return renderInput(params);
            }

            return (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={error}
                    helperText={helperText}
                    required={required}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    size={size}
                    variant={variant}
                />
            );
        },
        [
            renderInput,
            label,
            placeholder,
            error,
            helperText,
            required,
            disabled,
            fullWidth,
            size,
            variant,
        ]
    );

    // Don't use groupBy if we want selectable group headers
    // Instead, we'll manually handle the grouping in renderOption
    const shouldUseGroupBy = groupBy && !enableSelectableGroupHeaders;

    return (
        <Autocomplete
            multiple={multiple}
            disableCloseOnSelect={multiple && disableCloseOnSelect}
            options={autocompleteOptions}
            getOptionLabel={(option) => option.label}
            groupBy={shouldUseGroupBy ? groupBy : undefined}
            value={autocompleteValue}
            onChange={handleChange}
            renderOption={defaultRenderOption}
            renderTags={multiple ? defaultRenderTags : undefined}
            renderInput={defaultRenderInput}
            filterOptions={filterOptions}
            noOptionsText={noOptionsText}
            loading={loading}
            loadingText={loadingText}
            disabled={disabled}
            fullWidth={fullWidth}
            size={size}
            sx={sx}
            // Additional props for better UX and performance
            clearOnEscape
            selectOnFocus
            handleHomeEndKeys
            // Performance optimizations for large datasets
            ListboxProps={{
                style: {
                    maxHeight: "300px", // Limit height to prevent performance issues
                },
            }}
            // Enable virtualization for very large lists (if supported by MUI version)
            {...(autocompleteOptions.length > 1000 && {
                ListboxProps: {
                    style: {
                        maxHeight: "300px",
                    },
                },
            })}
        />
    );
}

// Helper function to create options from simple arrays
export function createOptionsFromArray<T>(
    items: T[],
    getLabel: (item: T) => string,
    getValue: (item: T) => string | number,
    getGroup?: (item: T) => string
): AppAutoCompleteOption<T>[] {
    return items.map((item) => ({
        id: getValue(item),
        label: getLabel(item),
        value: item,
        group: getGroup?.(item),
    }));
}

// Helper function to create options from key-value pairs
export function createOptionsFromKeyValue<T>(
    items: Record<string, T>
): AppAutoCompleteOption<T>[] {
    return Object.entries(items).map(([key, value]) => ({
        id: key,
        label: value as string,
        value: value,
    }));
}
